// "Trivial solve" detector — did the player crack the puzzle essentially instantly?
//
// On such solves the passive 5s reflection is a wasted opportunity: there's an aha to catch
// while it's fresh. We swap it for an active, untimed retrospective (see InstantRetro). A solve
// counts as trivial when the player SAID the correct answer (so "Nice, that was quick!" never
// fires on a wrong/absent guess) AND either: they committed within 10s, OR — after stripping
// the answer's own words, "commit", and common fillers — ≤ 2 content words of think-aloud
// remain (they blurted it with almost no reasoning). Domain-agnostic: both cryptic and rebus
// pass their committed transcript + the spoken-answer match.

const STOP = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'so', 'to', 'of', 'in', 'on', 'it', 'its', 'is', 'be',
  'as', 'at', 'i', 'im', 'ive', 'id', 'you', 'that', 'this', 'um', 'uh', 'er', 'hmm', 'ah', 'oh',
  'ok', 'okay', 'yeah', 'yes', 'no', 'commit', 'answer', 'maybe', 'like', 'just', 'well', 'right',
  'got', 'get', 'gonna', 'wanna', 'lets',
])

export const TRIVIAL_SOLVE_MS = 10_000

const tokens = (s: string): string[] => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)

// Content words remaining after removing the answer's own words, "commit", and common fillers.
export const residualContentWords = (transcript: string, answer: string): string[] => {
  const answerToks = new Set(tokens(answer))
  return tokens(transcript).filter((w) => w.length >= 2 && !answerToks.has(w) && !STOP.has(w))
}

export type TrivialSolveInput = {
  committedTranscript: string
  matchedAnswer: string | null // correct answer the SPOKEN transcript matched (null = not said)
  solveMs: number
}

export const isTrivialSolve = ({ committedTranscript, matchedAnswer, solveMs }: TrivialSolveInput): boolean => {
  if (!matchedAnswer) return false
  if (solveMs <= TRIVIAL_SOLVE_MS) return true
  return residualContentWords(committedTranscript, matchedAnswer).length <= 2
}
